
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { sharedApi } from '../../api/sharedApi';
import { useNavigate, Navigate, useParams } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Loader2, Search, Lock } from 'lucide-react';
import { toast } from 'sonner';

// Reusable Access Denied / Not Found Component
const AccessDeniedState = ({ resource }) => (
    <div className="flex flex-col items-center justify-center py-12 text-center bg-gray-50 rounded-lg border border-gray-100">
        <Lock className="h-10 w-10 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">No disponible</h3>
        <p className="text-gray-500 max-w-sm">
            La información solicitada no está disponible.
        </p>
    </div>
);

export const SharedLawFirmDetail = () => {
    const { id: sourceTenantId } = useParams();
    const { token, isCompany } = useAuth();
    // Calculate gating boolean first
    const isCompanyUser = isCompany();

    // -- HOOKS MUST BE UNCONDITIONAL --
    const [activeTab, setActiveTab] = useState("trademarks");

    // Trademarks State
    const [trademarks, setTrademarks] = useState([]);
    const [tmLoading, setTmLoading] = useState(false);
    const [tmError, setTmError] = useState(null); // '404' or 'other'
    const [tmPage, setTmPage] = useState(1);
    const [tmQuery, setTmQuery] = useState("");

    // Procedures State
    const [procedures, setProcedures] = useState([]);
    const [procLoading, setProcLoading] = useState(false);
    const [procError, setProcError] = useState(null);
    const [procPage, setProcPage] = useState(1);

    const fetchTrademarks = async () => {
        if (!isCompanyUser) return;
        setTmLoading(true);
        setTmError(null);
        try {
            const data = await sharedApi.getTrademarks(token, sourceTenantId, { page: tmPage, query: tmQuery });
            setTrademarks(data);
        } catch (error) {
            if (error.response?.status === 404) {
                setTmError('404'); // Scope missing or link missing (Anti-leak)
            } else {
                toast.error("Error cargando marcas compartidas");
                setTmError('other');
            }
        } finally {
            setTmLoading(false);
        }
    };

    const fetchProcedures = async () => {
        if (!isCompanyUser) return;
        setProcLoading(true);
        setProcError(null);
        try {
            const data = await sharedApi.getProcedures(token, sourceTenantId, { page: procPage });
            setProcedures(data);
        } catch (error) {
            if (error.response?.status === 404) {
                setProcError('404');
            } else {
                toast.error("Error cargando procedimientos compartidos");
                setProcError('other');
            }
        } finally {
            setProcLoading(false);
        }
    };

    const handleTmSearch = (e) => {
        e.preventDefault();
        setTmPage(1); // Reset page
        fetchTrademarks();
    };

    useEffect(() => {
        if (!isCompanyUser) return;

        if (activeTab === 'trademarks') {
            fetchTrademarks();
        } else if (activeTab === 'procedures') {
            fetchProcedures();
        }
    }, [activeTab, tmPage, procPage, isCompanyUser]); // Query triggers separately via enter or button

    // -- NOW DO GATING --
    if (!isCompanyUser) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <>
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900">Detalle de Despacho</h1>
                    <p className="text-muted-foreground text-sm">ID: {sourceTenantId}</p>
                </div>

                {/* Simple Tabs Implementation */}
                <div className="border-b border-gray-200 mb-4">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('trademarks')}
                            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'trademarks' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        >
                            Marcas
                        </button>
                        <button
                            onClick={() => setActiveTab('procedures')}
                            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'procedures' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        >
                            Procedimientos
                        </button>
                    </nav>
                </div>

                {/* Content Area */}
                <div>
                    {activeTab === 'trademarks' && (
                        <div className="space-y-4">
                            {!tmError && (
                                <form onSubmit={handleTmSearch} className="flex gap-2 max-w-md">
                                    <Input
                                        placeholder="Buscar por denominación..."
                                        value={tmQuery}
                                        onChange={(e) => setTmQuery(e.target.value)}
                                    />
                                    <Button type="submit" variant="secondary"><Search className="h-4 w-4" /></Button>
                                </form>
                            )}

                            {tmLoading ? (
                                <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>
                            ) : tmError === '404' ? (
                                <AccessDeniedState resource="marcas" />
                            ) : (
                                <div className="border rounded-md">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Denominación</TableHead>
                                                <TableHead>Expediente</TableHead>
                                                <TableHead>Clases</TableHead>
                                                <TableHead>Estatus</TableHead>
                                                <TableHead>Vigencia</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {trademarks.map(tm => (
                                                <TableRow key={tm.id}>
                                                    <TableCell className="font-medium">{tm.denominacion}</TableCell>
                                                    <TableCell>{tm.expediente}</TableCell>
                                                    <TableCell>{tm.clases?.join(', ')}</TableCell>
                                                    <TableCell>{tm.estatus_impi_crudo}</TableCell>
                                                    <TableCell>{tm.fecha_vigencia ? new Date(tm.fecha_vigencia).toLocaleDateString() : '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                            {trademarks.length === 0 && (
                                                <TableRow><TableCell colSpan={5} className="text-center py-4 text-gray-500">No se encontraron marcas</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                            {/* Simple Pagination controls */}
                            {!tmError && trademarks.length > 0 && (
                                <div className="flex justify-end gap-2 mt-2">
                                    <Button variant="outline" size="sm" onClick={() => setTmPage(p => Math.max(1, p - 1))} disabled={tmPage === 1}>Anterior</Button>
                                    <Button variant="outline" size="sm" onClick={() => setTmPage(p => p + 1)} disabled={trademarks.length < 25}>Siguiente</Button>
                                </div>
                            )}
                        </div>
                    )} {/* End Trademarks Tab */}

                    {activeTab === 'procedures' && (
                        <div className="space-y-4">
                            {procLoading ? (
                                <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>
                            ) : procError === '404' ? (
                                <AccessDeniedState resource="procedimientos" />
                            ) : (
                                <div className="border rounded-md">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Título</TableHead>
                                                <TableHead>Tipo</TableHead>
                                                <TableHead>Estado</TableHead>
                                                <TableHead>Prioridad</TableHead>
                                                <TableHead>Fecha Act.</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {procedures.map(proc => (
                                                <TableRow key={proc.id}>
                                                    <TableCell className="font-medium">{proc.title}</TableCell>
                                                    <TableCell>{proc.procedure_type}</TableCell>
                                                    <TableCell><Badge variant="outline">{proc.status}</Badge></TableCell>
                                                    <TableCell>{proc.priority}</TableCell>
                                                    <TableCell>{proc.updated_at ? new Date(proc.updated_at).toLocaleDateString() : '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                            {procedures.length === 0 && (
                                                <TableRow><TableCell colSpan={5} className="text-center py-4 text-gray-500">No se encontraron procedimientos</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                            {/* Simple Pagination controls */}
                            {!procError && procedures.length > 0 && (
                                <div className="flex justify-end gap-2 mt-2">
                                    <Button variant="outline" size="sm" onClick={() => setProcPage(p => Math.max(1, p - 1))} disabled={procPage === 1}>Anterior</Button>
                                    <Button variant="outline" size="sm" onClick={() => setProcPage(p => p + 1)} disabled={procedures.length < 25}>Siguiente</Button>
                                </div>
                            )}
                        </div>
                    )} {/* End Procedures Tab */}

                </div>
            </div>
        </>
    );
};
